import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isFunction from 'lodash/isFunction';
import { saveAs } from 'file-saver';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import moment from 'moment';
import I18n from 'i18n-js';
import formatCurrency from 'common/utils/format-currency.js';
import {
  ORDER_STATE,
  EXCEL_TABLE_VALUE,
  DELIVERY_STATUS_MAP,
  IN_TRANSIT_DELIVERY_STATUS,
  HISTORY_REPORT_TABLE_COLS,
} from 'constants/history-constants';
import {
  POSTPAID_BILLING_TYPE,
  FIELD_PLACEHOLDER,
} from 'constants/booking-constants';
import { isSameDayDelivery } from 'helper/history-order';
import {
  is12HourFormat,
  renderTimeZone,
  convertTimeZoneUnix,
} from 'common/utils/convert-time-zone';
import logo from 'public/images/logo-excel.png';
import { getDefaultErrorContext } from 'common/utils/rum-utils';

const PAID_BY_CASH = 'CASH';
function toDataURL(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    /* eslint-disable func-names */
    xhr.onload = function() {
      const reader = new FileReader();
      reader.onloadend = function() {
        resolve(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    /* eslint-enable func-names */
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
}
const logoPromise = toDataURL(logo);

function eachColumnInRange(ws, col1, col2, cb) {
  for (let c = col1; c <= col2; c += 1) {
    const col = ws.getColumn(c);
    cb(col);
  }
}

const adjustColumns = ws => {
  eachColumnInRange(ws, 1, ws.columnCount, column => {
    let maxWidth = 10;
    if (
      column.letter === 'C' ||
      column.letter === 'D' ||
      column.letter === 'E' ||
      column.letter === 'F' ||
      column.letter === 'G'
    ) {
      maxWidth = 40;
      column.width = maxWidth; //eslint-disable-line
    } else if (
      column.letter === 'H' ||
      column.letter === 'I' ||
      column.letter === 'K'
    ) {
      maxWidth = 50;
      column.width = maxWidth; //eslint-disable-line
    } else if (
      column.letter === 'F' ||
      column.letter === 'J' ||
      column.letter === 'L' ||
      column.letter === 'M' ||
      column.letter === 'N' ||
      column.letter === 'O' ||
      column.letter === 'P' ||
      column.letter === 'Q' ||
      column.letter === 'R' ||
      column.letter === 'S' ||
      column.letter === 'T' ||
      column.letter === 'U' ||
      column.letter === 'V' ||
      column.letter === 'W' ||
      column.letter === 'X' ||
      column.letter === 'AA' ||
      column.letter === 'AB' ||
      column.letter === 'AC' ||
      column.letter === 'AD'
    ) {
      maxWidth = 28;
      column.width = maxWidth; //eslint-disable-line
    } else if (column.letter === 'Y' || column.letter === 'Z') {
      maxWidth = 35;
      column.width = maxWidth; //eslint-disable-line
    }
  });
};

const handleFreezeView = sheet => {
  sheet.views = [
    //eslint-disable-line
    {
      state: 'frozen',
      ySplit: 6,
    },
  ];
};

const generateReportInfo = (
  sheet,
  userInfo,
  companyInfo,
  fromDate,
  toDate,
  orderState,
  lastUpdatedMoment,
) => {
  for (let i = 2; i < 5; i += 1) {
    const cell = sheet.getCell(`C${i}`);
    if (i === 2) {
      cell.value = `${userInfo} • ${companyInfo}`;
    } else if (i === 3) {
      cell.value = `Activity from ${fromDate} - ${toDate}`;
    } else if (i === 4) {
      cell.value = `${I18n.t(ORDER_STATE[orderState])} • ${I18n.t(
        'history.latestUpdated.pluralDelivery',
      )} ${lastUpdatedMoment}`;
    }
    cell.font = {
      size: 14,
    };
  }
};

const generateTableHeading = sheet => {
  const historyTableHeaderRow = 6;
  HISTORY_REPORT_TABLE_COLS.forEach(col => {
    const cell = sheet.getCell(`${col}${historyTableHeaderRow}`);
    if (col === 'B') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE['#']);
    } else if (col === 'C') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.dateCreated);
    } else if (col === 'D') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.orderID);
    } else if (col === 'E') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.scheduledTime);
    } else if (col === 'F') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.pickUpTime);
    } else if (col === 'G') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.deliveredTime);
    } else if (col === 'H') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.cancelledTime);
    } else if (col === 'I') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.deliveryInstruction);
    } else if (col === 'J') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.deliveryStatus);
    } else if (col === 'K') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.senderName);
    } else if (col === 'L') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.senderMobileNumber);
    } else if (col === 'M') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.senderAddress);
    } else if (col === 'N') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.aboutSenderDetails);
    } else if (col === 'O') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.recipientName);
    } else if (col === 'P') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.recipientMobileNumber);
    } else if (col === 'Q') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.recipientAddress);
    } else if (col === 'R') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.aboutReceiverDetails);
    } else if (col === 'S') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.serviceType);
    } else if (col === 'T') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.itemAmount);
    } else if (col === 'U') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.deliveryFee);
    } else if (col === 'V') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.paymentMethod);
    } else if (col === 'W') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.promo);
    } else if (col === 'X') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.trackingLink);
    } else if (col === 'Y') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.driverDetails);
    } else if (col === 'Z') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.viewReceipt);
    } else if (col === 'AA') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.preDiscounted);
    } else if (col === 'AB') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.postDiscounted);
    } else if (col === 'AC') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.discountAmount);
    } else if (col === 'AD') {
      cell.value = I18n.t(EXCEL_TABLE_VALUE.referenceCode);
    }

    cell.font = {
      size: 14,
      bold: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '00FDF3D0' },
    };
  });
};

function calculateAmount(amount, currency) {
  return amount * 10 ** (currency.exponent * -1);
}

const isV1Order = order => !_isEmpty(_get(order, 'sender'));

const getSenderToExport = order => {
  if (_isEmpty(order)) return {};

  const isOrderV1 = isV1Order(order);

  if (isOrderV1) {
    return {
      ...order.sender,
      ...order.origin,
    };
  }

  const senderV2 = _get(order, 'steps[0]');
  return {
    ...senderV2.contact,
    ...senderV2.location,
  };
};

const getRecipientsToExport = order => {
  if (_isEmpty(order)) return {};

  const isOrderV1 = isV1Order(order);

  if (isOrderV1) {
    return [
      {
        ...order.receiver,
        ...order.destination,
      },
    ];
  }

  const recipientsV2 = order.steps.slice(1);
  return recipientsV2.map(recipient => ({
    ...recipient.contact,
    ...recipient.location,
    itemInfo: recipient.itemInfo, // for future use
    receiptLink: recipient.orderReceiptLink,
  }));
};

const deliveryCompleted = deliveryInfo => deliveryInfo.status === 'COMPLETED';

const getReceiptLinks = (deliveryInfo, recipients) => {
  if (isV1Order(deliveryInfo)) {
    const receiptLink = _get(deliveryInfo, 'receiptLink', '');
    if (_isEmpty(receiptLink) && deliveryCompleted(deliveryInfo)) {
      return I18n.t('history.table.expired');
    }
    return receiptLink;
  }

  return recipients.reduce((acc, recipient, index) => {
    const receiptLink = _get(recipient, 'receiptLink', '');
    if (_isEmpty(receiptLink) && deliveryCompleted(deliveryInfo)) {
      return acc.concat(
        `#${index + 1} \n${I18n.t('history.table.expired')} \n\n\n`,
      );
    }
    return acc.concat(`#${index + 1} \n${recipient.receiptLink} \n\n\n`);
  }, '');
};

const getTrackingLinks = deliveryInfo => {
  if (isV1Order(deliveryInfo)) {
    const { trackingURL } = deliveryInfo;
    if (_isEmpty(trackingURL) && deliveryCompleted(deliveryInfo)) {
      return I18n.t('history.table.expired');
    }
    return trackingURL;
  }

  const trackingLinksMap = _get(deliveryInfo, 'webTrackingURLsMap', []);
  return trackingLinksMap.reduce((acc, linkMap, index) => {
    const orderID = _get(linkMap, 'orderID');
    const webTrackingURL = _get(linkMap, 'webTrackingURL', '');
    if (_isEmpty(webTrackingURL) && deliveryCompleted(deliveryInfo)) {
      return acc.concat(
        `orderID: ${orderID} \n${I18n.t('history.table.expired')} \n\n\n`,
      );
    }
    return acc.concat(
      `orderID: ${orderID} \nTracking link: ${webTrackingURL} \n\n\n`,
    );
  }, '');
};

const getDriverInfo = delivery => {
  if (!delivery.vehicle) return '--';
  const { vehicle: { model, plateNumber } } = delivery;
  if (model) return `${model} ${plateNumber}`;
  return plateNumber || '--';
};

const getCOD = deliveryInfo => {
  const codFilter = deliveryInfo.steps.filter(
    step => _get(step, 'cashOnDelivery.enable', false) == true,
  );
  if (codFilter.length > 0) {
    const totalCOD = formatCurrency({
      code: _get(deliveryInfo, 'currency.code'),
      amount: codFilter.reduce(
        (total, obj) => _get(obj, 'cashOnDelivery.amount', 0) + total,
        0,
      ),
      symbol: _get(deliveryInfo, 'currency.symbol'),
      hideSymbol: false,
    });
    const richText = [
      {
        text: codFilter.reduce(
          (text, obj) =>
            text.concat(
              formatCurrency({
                code: _get(deliveryInfo, 'currency.code'),
                amount: _get(obj, 'cashOnDelivery.amount', 0),
                symbol: _get(deliveryInfo, 'currency.symbol'),
                hideSymbol: false,
              }).concat('\r\n'),
            ),
          '',
        ),
        font: {
          size: 14,
          bold: false,
          name: 'Calibri',
        },
      },
    ];
    if (codFilter.length > 1) {
      const totalField = {
        text: totalCOD.concat(' (Total)'),
        font: {
          bold: true,
          size: 14,
          name: 'Calibri',
        },
      };
      richText.push(totalField);
    }
    return {
      richText,
    };
  }

  return '';
};

const generateTableContent = (sheet, deliveries, city) => {
  for (let i = 0; i < deliveries.length; i += 1) {
    const deliveryInfo = deliveries[i];
    const sender = getSenderToExport(deliveryInfo);
    const recipients = getRecipientsToExport(deliveryInfo);
    const createdTime = deliveryInfo.createMeta.createdAt;
    const isTimeIn12HourFormat = is12HourFormat(createdTime);
    const rowStarted = i + 7;

    const getOrderEventCellValue = (order, desiredState) => {
      const { orderEvents } = order;
      if (_isEmpty(orderEvents)) return '';
      const stateMatched = orderEvents.find(
        event => event.State === desiredState,
      );

      return stateMatched
        ? `${convertTimeZoneUnix(
            isTimeIn12HourFormat,
            city,
            stateMatched.Timestamp,
          )}`
        : '';
    };
    const defaultAlign = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true,
    };
    const defaultFont = {
      size: 14,
    };
    const sheetCol = {
      B: {
        value: i + 1,
      },
      C: {
        value: `${renderTimeZone(createdTime, city)} \n ${
          deliveryInfo.createMeta.createdBy
        }`,
      },
      D: {
        value: () =>
          `Order Id: ${
            isSameDayDelivery(deliveryInfo.deliveryID)
              ? deliveryInfo.deliveryID
              : deliveryInfo.bookingCode
          }`,
      },
      E: {
        value: getOrderEventCellValue(deliveryInfo, ORDER_STATE.SCHEDULING),
      },
      F: {
        value: getOrderEventCellValue(deliveryInfo, 'PENDING_DROP_OFF'),
      },
      G: {
        value: getOrderEventCellValue(deliveryInfo, 'COMPLETED'),
      },
      H: {
        value: () => {
          const senderCancelledValue = getOrderEventCellValue(
            deliveryInfo,
            'SENDER_CANCELLED',
          );
          const driverCancelledValue = getOrderEventCellValue(
            deliveryInfo,
            'DRIVER_CANCELLED',
          );
          return senderCancelledValue || driverCancelledValue;
        },
      },
      I: {
        value: deliveryInfo.noteToDriver || '',
      },
      J: {
        value: I18n.t(DELIVERY_STATUS_MAP[deliveryInfo.status]),
      },
      K: {
        value: sender.name,
      },
      L: {
        value: sender.phone,
      },
      M: {
        value: `${sender.keywords} • ${sender.address}`,
      },
      N: {
        value: sender.instruction || '',
      },
      O: {
        value: recipients.reduce(
          (acc, recipient) => acc.concat(`${recipient.name} \n\n\n`),
          '',
        ),
      },
      P: {
        value: recipients.reduce(
          (acc, recipient) => acc.concat(`${recipient.phone} \n\n\n`),
          '',
        ),
      },
      Q: {
        value: recipients.reduce(
          (acc, recipient) =>
            acc.concat(`${recipient.keywords} • ${recipient.address} \n\n`),
          '',
        ),
      },
      R: {
        value: recipients.reduce(
          (acc, recipient) =>
            acc.concat(`${recipient.instruction || ''} \n\n\n`),
          '',
        ),
      },
      S: {
        value: _get(deliveryInfo, 'service.serviceName'),
        font: {
          size: 14,
          bold: true,
        },
      },
      T: {
        value: getCOD(deliveryInfo),
      },
      U: {
        value: formatCurrency({
          code: deliveryInfo.currency.code,
          amount: calculateAmount(
            _get(deliveryInfo, 'amount', 0), // https://jira.grab.com/browse/GFOM-9285
            deliveryInfo.currency,
          ),
          symbol: deliveryInfo.currency.symbol,
        }),
        font: {
          size: 14,
          bold: true,
        },
      },
      V: {
        value: () => {
          let { paidBy, name, type } = _get(deliveryInfo, 'paymentMethod', {});
          if (isV1Order(deliveryInfo)) {
            paidBy = deliveryInfo.paidBy;
            name = deliveryInfo.paymentMethodName;
            type = deliveryInfo.paymentMethodType;
          }
          if (type === POSTPAID_BILLING_TYPE) {
            return I18n.t(FIELD_PLACEHOLDER.CORP_BILLING);
          }
          if (name === PAID_BY_CASH && paidBy === 'RECIPIENT') {
            return I18n.t('booking.paymentMethod.cashByRecipient');
          }
          if (name === PAID_BY_CASH && paidBy === 'SENDER') {
            return I18n.t('booking.paymentMethod.cashBySender');
          }

          return `${type} ${name}`;
        },
      },
      W: {
        value: _get(deliveryInfo, 'service.promoCode', '--'),
      },
      X: {
        value: getTrackingLinks(deliveryInfo),
      },
      Y: {
        value: getDriverInfo(deliveryInfo),
      },
      Z: {
        value: getReceiptLinks(deliveryInfo, recipients),
      },
      AA: {
        value: () => {
          const { discountDetail, currency } = deliveryInfo;
          const amount = _get(deliveryInfo, 'amount', 0); // https://jira.grab.com/browse/GFOM-9285
          if (_isEmpty(discountDetail)) return '--';

          return formatCurrency({
            code: currency.code,
            amount: calculateAmount(
              discountDetail.discountAmount + amount,
              currency,
            ),
            symbol: currency.symbol,
          });
        },
      },
      AB: {
        value: () => {
          const { discountDetail, currency } = deliveryInfo;
          const amount = _get(deliveryInfo, 'amount', 0); // https://jira.grab.com/browse/GFOM-9285
          if (_isEmpty(discountDetail)) return '--';
          return formatCurrency({
            code: currency.code,
            amount: calculateAmount(amount, currency),
            symbol: currency.symbol,
          });
        },
      },
      AC: {
        value: () => {
          const { discountDetail, currency } = deliveryInfo;
          if (_isEmpty(discountDetail)) return '--';
          return formatCurrency({
            code: currency.code,
            amount: calculateAmount(discountDetail.discountAmount, currency),
            symbol: currency.symbol,
          });
        },
      },
      AD: {
        value: () => _get(deliveryInfo, 'referenceCode', '--'),
      },
    };

    Object.keys(sheetCol).forEach(col => {
      const cell = sheet.getCell(`${col}${rowStarted}`);
      const { value, font, alignment } = sheetCol[col];
      cell.value = _isFunction(value) ? value() : value;
      cell.font = _isEmpty(font) ? defaultFont : font;
      cell.alignment = _isEmpty(alignment) ? defaultAlign : alignment;
    });
  }
};

// Do not show in-transit deliveries in report to align with GDM changes
// https://jira.grab.com/browse/GFOM-6842
const filterInTransitDeliveries = deliveries =>
  deliveries.filter(
    delivery => !IN_TRANSIT_DELIVERY_STATUS.includes(delivery.status),
  );

const generateExcel = async (
  userInfo,
  companyInfo,
  fromDate,
  toDate,
  orderState,
  lastUpdatedMoment,
  deliveries,
  city,
) => {
  try {
    const ExcelModule = await import('exceljs/dist/exceljs');
    const Excel = ExcelModule.default;

    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    const imageSrc = await logoPromise;
    const imageId1 = workbook.addImage({
      base64: imageSrc,
      extension: 'png',
    });
    sheet.addImage(imageId1, 'A2:B3');
    generateReportInfo(
      sheet,
      userInfo,
      companyInfo,
      fromDate,
      toDate,
      orderState,
      lastUpdatedMoment,
    );
    const filteredDeliveries = filterInTransitDeliveries(deliveries);
    generateTableHeading(sheet);
    generateTableContent(sheet, filteredDeliveries, city);
    adjustColumns(sheet);

    handleFreezeView(sheet);
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(
        blob,
        `GrabExpress Report_${moment().format('HH:mm DD/MM/YYYY')}.xlsx`,
      );
    });
  } catch (error) {
    const errorCtx = rumErrorCtx(getDefaultErrorContext())
      .component('generateExcel')
      .message(error.message || 'Failed to generate history report excel file')
      .severity(SEVERITY.MEDIUM)
      .build();
    ddRum.addError(error, errorCtx);
  }
};

export default generateExcel;
